import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X, Search } from 'lucide-react';
import api from '../utils/api';

/**
 * UserTypeahead — debounced multi-select user picker.
 *
 * Replaces "type an email → click Resolve" flows in Broadcast and similar
 * admin tools. Behaves like Gmail/Slack recipient picker:
 *   - debounced `q` search hits /api/admin/users/search
 *   - results dropdown shows photo + name + email + secondary meta
 *   - click a result → chip is added, dropdown stays open for multi-select
 *   - chips have an inline X to remove
 *   - empty query shows the most recent users (default dropdown state)
 *
 * Intentionally uncontrolled-ish: parent supplies `value` (the selected
 * users array) + `onChange(nextArray)`, and the search box is internal state.
 */
const UserTypeahead = ({
  value = [],
  onChange,
  placeholder = 'Search by name, email, or phone',
  roles = null,              // optional: 'talent' | 'client,admin' ...
  limit = 20,
  debounceMs = 350,
  'data-testid': testId = 'user-typeahead',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const selectedIds = new Set(value.map(u => u.id));

  // Debounced search. We fire on every keystroke (even empty) after the
  // debounce so the user gets a default "recent users" list if they just
  // focus the input.
  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('limit', String(limit));
      if (roles) params.set('roles', roles);
      const { data } = await api.get(`/admin/users/search?${params.toString()}`);
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [limit, roles]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, debounceMs, runSearch]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current || containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const addUser = (user) => {
    if (selectedIds.has(user.id)) return;
    onChange([...value, user]);
    // Stay focused for rapid multi-select; clear the query so the next
    // keystroke starts fresh.
    setQuery('');
  };

  const removeUser = (userId) => {
    onChange(value.filter(u => u.id !== userId));
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 p-2 border rounded-md bg-white min-h-[42px] focus-within:ring-2 focus-within:ring-cyan-500 focus-within:border-cyan-500">
        {value.map(u => (
          <span
            key={u.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 border border-cyan-200 rounded-full text-xs text-cyan-800"
            data-testid={`${testId}-chip-${u.id}`}
          >
            {u.profile_photo && (
              <img src={u.profile_photo} alt="" className="w-4 h-4 rounded-full object-cover" />
            )}
            <span className="font-medium">{u.full_name || u.email}</span>
            <button
              type="button"
              onClick={() => removeUser(u.id)}
              className="hover:bg-cyan-100 rounded-full p-0.5"
              aria-label={`Remove ${u.full_name || u.email}`}
              data-testid={`${testId}-chip-remove-${u.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={value.length === 0 ? placeholder : 'Add more…'}
          className="flex-1 min-w-[180px] text-sm outline-none bg-transparent"
          data-testid={`${testId}-input`}
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 self-center" />}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto bg-white border rounded-md shadow-lg" data-testid={`${testId}-dropdown`}>
          {results.length === 0 ? (
            <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
              <Search className="w-3.5 h-3.5" />
              {query ? 'No matches — try a different spelling or email.' : 'Start typing a name, email, or phone number.'}
            </div>
          ) : (
            <ul className="py-1">
              {results.map(u => {
                const already = selectedIds.has(u.id);
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      disabled={already}
                      onClick={() => addUser(u)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50 ${already ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`${testId}-option-${u.id}`}
                    >
                      {u.profile_photo ? (
                        <img src={u.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-semibold">
                          {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{u.full_name || u.email}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {u.email}
                          {u.location ? ` · ${u.location}` : ''}
                          {u.role ? ` · ${u.role}` : ''}
                        </div>
                      </div>
                      {already && <span className="text-[10px] text-emerald-600 font-semibold">Added</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UserTypeahead;
